// Baku Gate — Date/Time Utility
// Azerbaijan Standard Time: UTC+4 (Asia/Baku), no DST
// Uses Intl.DateTimeFormat for reliable timezone conversion in all modern browsers.

const BAKU_TZ = 'Asia/Baku';

let _intlSupported = null;
function intlSupported() {
  if (_intlSupported !== null) return _intlSupported;
  try {
    new Intl.DateTimeFormat('en', { timeZone: BAKU_TZ }).format(new Date());
    _intlSupported = true;
  } catch {
    _intlSupported = false;
  }
  return _intlSupported;
}

function bakuParts(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (!d || isNaN(d.getTime())) return null;

  if (intlSupported()) {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: BAKU_TZ,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(d);

    const get = (t) => parts.find(p => p.type === t)?.value || '00';
    const HH = get('hour') === '24' ? '00' : get('hour');
    return { dd: get('day'), mm: get('month'), yyyy: get('year'), HH, MM: get('minute'), SS: get('second') };
  }

  // Fallback: manual UTC+4 shift
  const b = new Date(d.getTime() + 4 * 3600 * 1000);
  const p = n => String(n).padStart(2, '0');
  return {
    dd: p(b.getUTCDate()), mm: p(b.getUTCMonth() + 1), yyyy: String(b.getUTCFullYear()),
    HH: p(b.getUTCHours()), MM: p(b.getUTCMinutes()), SS: p(b.getUTCSeconds()),
  };
}

export function formatDate(date, format = 'datetime') {
  if (!date) return '—';
  const p = bakuParts(date);
  if (!p) return '—';

  switch (format) {
    case 'date':        return `${p.dd}.${p.mm}.${p.yyyy}`;
    case 'time':        return `${p.HH}:${p.MM}`;
    case 'timesec':     return `${p.HH}:${p.MM}:${p.SS}`;
    case 'datetime':    return `${p.dd}.${p.mm}.${p.yyyy}, ${p.HH}:${p.MM}`;
    case 'datetimesec': return `${p.dd}.${p.mm}.${p.yyyy}, ${p.HH}:${p.MM}:${p.SS}`;
    case 'full':        return `${p.dd}.${p.mm}.${p.yyyy}, ${p.HH}:${p.MM}:${p.SS}`;
    case 'compact': {
      const np = bakuParts(new Date());
      const today = np && p.dd === np.dd && p.mm === np.mm && p.yyyy === np.yyyy;
      return today ? `${p.HH}:${p.MM}:${p.SS}` : `${p.dd}.${p.mm}, ${p.HH}:${p.MM}`;
    }
    case 'relative': return relativeTime(date);
    default: return `${p.dd}.${p.mm}.${p.yyyy}, ${p.HH}:${p.MM}`;
  }
}

export function relativeTime(date) {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const abs  = Math.abs(diff);
  const future = diff < 0;
  const secs = Math.floor(abs / 1000);
  const mins = Math.floor(abs / 60000);
  const hrs  = Math.floor(abs / 3600000);
  const days = Math.floor(abs / 86400000);
  let result;
  if (secs < 10)       result = 'İndicə';
  else if (secs < 60)  result = `${secs} san`;
  else if (mins < 60)  result = `${mins} dəq`;
  else if (hrs  < 24)  result = `${hrs} saat`;
  else if (days <  7)  result = `${days} gün`;
  else if (days < 30)  result = `${Math.floor(days / 7)} həftə`;
  else if (days < 365) result = `${Math.floor(days / 30)} ay`;
  else                 result = `${Math.floor(days / 365)} il`;
  if (result === 'İndicə') return result;
  return future ? `${result} sonra` : `${result} əvvəl`;
}

export function formatDuration(ms) {
  if (ms == null || ms < 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}d ${s}s`;
}

export function formatUptime(seconds) {
  if (!seconds && seconds !== 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (d > 0) return `${d}g ${h}s ${m}d`;
  if (h > 0) return `${h}s ${m}d`;
  if (m > 0) return `${m}d ${s}s`;
  return `${s}s`;
}

export function nowBaku(format = 'datetimesec') {
  return formatDate(new Date(), format);
}

export function dateRangeToApi(from, to) {
  return {
    from: from ? new Date(from).toISOString() : undefined,
    to:   to   ? new Date(to + 'T23:59:59').toISOString() : undefined,
  };
}
